import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import AnimateHeight from 'react-animate-height';

import { useTranslation } from 'next-i18next';
import classNames from 'classnames';

import AnchorLink from '../AnchorLink';
import Button from '../Button';
import RawHtml from '../RawHtml';

import styles from './Faq.module.scss';

const Faq = ({ id = '', heading = '', anchorLink = '', items = [], modifier = '', isRekAI }) => {
    const baseId = id ? `faq-${id}` : 'faq-list';

    const classes = classNames(styles['Faq'], {
        [styles[`Faq--${modifier}`]]: modifier,
        [styles['Faq--RekAI']]: isRekAI,
    });
    return (
        <div className={classes}>
            <div className={styles['Faq__Container']}>
                {heading && (
                    <h2 className={styles['Faq__Heading']}>
                        <AnchorLink className={styles['Faq__Heading']} title={heading} id={anchorLink} />
                    </h2>
                )}
                <div className={styles['Faq__List']}>
                    {items.map((item, i) => (
                        <FaqItem
                            key={i}
                            {...item.value}
                            id={item.id ? `${baseId}-item-${item.id}` : `${baseId}-item-${i}`}
                            headingLevel={heading ? 3 : 2}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

Faq.propTypes = {
    id: PropTypes.string.isRequired,
    heading: PropTypes.string,
    anchorLink: PropTypes.string,
    items: PropTypes.array,
    modifier: PropTypes.string,
};

const FaqItem = ({ headingLevel = 3, id = '', question = '', answer = '', url = '', pagetitle = '', facts }) => {
    const { t } = useTranslation('common');
    const [expanded, setExpanded] = useState(false);
    const itemContainerRef = useRef(null);

    const toggleExpanded = () => setExpanded(!expanded);

    // https://www.w3.org/WAI/GL/wiki/Using_the_WAI-ARIA_aria-expanded_state_to_mark_expandable_and_collapsible_regions#Example_1:_Using_a_button_to_collapse_and_expand_a_region
    useEffect(() => {
        if (expanded) {
            itemContainerRef.current.focus();
        }
    }, [expanded]);

    const Heading = `h${headingLevel}`;

    /* eslint-disable indent */
    const itemClasses = classNames(styles['Faq__Item'], {
        [styles['Faq__Item--Expanded']]: expanded,
    });

    return (
        <article className={itemClasses}>
            <button
                className={styles['Faq__Question']}
                onClick={toggleExpanded}
                aria-controls={id}
                aria-expanded={expanded}
                id={`faq-button-${id}`}
                title={expanded ? t('faq.close') : t('faq.open')}>
                <Heading>{question}</Heading>
            </button>
            <AnimateHeight
                duration={350}
                height={expanded ? 'auto' : 0}
                id={id}
                role="region"
                tabIndex="-1"
                ref={itemContainerRef}
                className={styles['Faq__AnimationWrapper']}
                contentClassName={styles['Faq__Answer']}>
                <RawHtml html={answer} modifier="Inner" />

                {url && (
                    <div className={styles['Faq__Page']}>
                        {t('faq.readMore')}
                        <a className={styles['Faq__Link']} href={url}>
                            {pagetitle}
                        </a>
                    </div>
                )}

                {facts && facts?.title && facts?.richtext && <FaqFacts {...facts} headingLevel={headingLevel + 1} />}
            </AnimateHeight>
        </article>
    );
};

FaqItem.propTypes = {
    headingLevel: PropTypes.number.isRequired,
    id: PropTypes.string.isRequired,
    question: PropTypes.string.isRequired,
    answer: PropTypes.string.isRequired,
    url: PropTypes.string,
    pagetitle: PropTypes.string,
    facts: PropTypes.object,
};

const FaqFacts = ({ headingLevel = 4, title = '', richtext = '' }) => {
    const Heading = `h${headingLevel}`;

    return (
        <div className={styles['Faq__Facts']}>
            <Heading className={styles['Faq__FactsTitle']}>{title}</Heading>
            <RawHtml html={richtext} modifier="Inner" />
        </div>
    );
};

FaqFacts.propTypes = {
    headingLevel: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    richtext: PropTypes.string.isRequired,
};

export default Faq;
